import { TemplatePage } from './TemplateChrome'
import type { TemplateProps } from './types'

const steps = [
  { title: 'Check eligibility', text: 'Your permit must expire within the next ninety days, or have expired in the last year.' },
  { title: 'Gather your documents', text: 'You need your current permit number and a photo taken in the last six months.' },
  { title: 'Apply online', text: 'Most renewals take about twenty minutes. You can save and finish later.' },
  { title: 'Get a decision', text: 'We email a decision within ten business days.' },
]

const questions = [
  ['Is there a fee?', 'Yes. Fee waivers are available, and you can request one in the application.'],
  ['Can I renew by mail?', 'Yes. Print the renewal form, sign it and mail it with a check to the address on the form.'],
  ['What if my permit has been expired for more than a year?', 'Apply for a new permit instead of renewing.'],
]

/** One service, start to finish: who it is for, the steps, and a clear way to begin. */
export default function ServiceLanding({ mission, link, credit }: TemplateProps) {
  return (
    <TemplatePage mission={mission} link={link} credit={credit} current="Services">
      <div className="mds-container mds-page-layout">
        <nav className="mds-sidenav" aria-label="Services">
          <ul>
            <li>
              <a className="mds-sidenav__link" href={link('multi-step-form')}>
                Apply for a permit
              </a>
            </li>
            <li>
              <a className="mds-sidenav__link" href={link('service-landing')} aria-current="page">
                Renew a permit
              </a>
            </li>
            <li>
              <a className="mds-sidenav__link" href="#status">
                Check status
              </a>
            </li>
          </ul>
        </nav>
        <div className="mds-stack mds-page-article">
          <nav className="mds-breadcrumb" aria-label="Breadcrumb">
            <ol>
              <li>
                <a href={link('agency-home')}>Home</a>
              </li>
              <li>
                <a href={link('service-landing')}>Services</a>
              </li>
              <li aria-current="page">Renew a permit</li>
            </ol>
          </nav>
          <h1 className="mds-h1">Renew a permit</h1>
          <p className="mds-lead">Renew online up to ninety days before your permit expires.</p>
          <section className="mds-summary" aria-labelledby="renew-summary">
            <h2 className="mds-summary__title" id="renew-summary">
              Before you start
            </h2>
            <ul>
              <li>Have your current permit number ready.</li>
              <li>Renewing online takes about twenty minutes.</li>
              <li>You can save your progress and come back later.</li>
            </ul>
          </section>
          <h2 className="mds-h2">How to renew</h2>
          <ol className="mds-process">
            {steps.map((step) => (
              <li key={step.title} className="mds-process__item">
                <h3 className="mds-process__title">{step.title}</h3>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
          <p>
            <a className="mds-button mds-button--primary mds-button--lg" href={link('multi-step-form')}>
              Start your renewal
            </a>
          </p>
          <h2 className="mds-h2">Common questions</h2>
          <div className="mds-accordion">
            {questions.map(([question, answer]) => (
              <details key={question} className="mds-accordion__item">
                <summary className="mds-accordion__summary">
                  {question}
                  <span className="mds-accordion__icon" aria-hidden="true" />
                </summary>
                <p className="mds-accordion__body">{answer}</p>
              </details>
            ))}
          </div>
          <section id="contact" aria-labelledby="contact-title" className="mds-stack">
            <h2 id="contact-title" className="mds-h3">
              Need help?
            </h2>
            <p>
              Call 1-800-555-0100, Monday to Friday, 8 a.m. to 8 p.m. Eastern, or{' '}
              <a className="mds-link" href={link('search-results')}>
                search for answers
              </a>
              .
            </p>
          </section>
        </div>
      </div>
    </TemplatePage>
  )
}
